import { ExtractedRecord } from "@/types";

import { absoluteIndexOf } from "./recordIndexing";

/**
 * How many errored records still block approval, and whether that number is
 * exact (#6853, !4682 review).
 *
 * ## Why this is a module rather than a memo in DocumentReviewTab
 *
 * It was a memo, and the memo was WRONG in a way no test could reach. It read
 *
 *     record_summary.records_with_errors − summarizeRecords(loadedRecords)
 *
 * with exclusion state in neither term, justified by a comment: "a reviewer
 * also cannot exclude a record they have never been shown, so exclusions can
 * only ever apply to the loaded side." #6853's errored banner made that false
 * — it excludes records by absolute index, from a server read, whether or not
 * they were ever displayed. The consequence was that on exactly the document
 * the feature exists for (errors at 5,500 and 6,700, none loaded) the reviewer
 * excluded them, nothing the gate read moved, and Approve stayed disabled with
 * no path forward.
 *
 * Nothing rendered `DocumentReviewTab` in the test suite, so the arithmetic had
 * no assertion anywhere. Extracting it is what makes the fix checkable.
 */
export interface UnresolvedErrored {
  /** Errored records not yet resolved by an exclusion. Zero means approvable. */
  count: number;
  /**
   * False when `count` is a lower bound on what blocks — the errored read was
   * truncated, so records outside the sample are neither counted as excluded
   * nor resolvable from the banner.
   *
   * Affects COPY only. The gate is `count > 0` either way, which is the right
   * direction: an inexact count of blocking records must never unblock.
   */
  exact: boolean;
}

/**
 * Errored records the server reported, minus the ones the reviewer excluded.
 *
 * A set difference over absolute `record_index`, which is what both sides are
 * keyed on. Deliberately NOT a refetch: exclusions are client state until
 * approve, so `?errored=true` would return the same total and the divergence
 * would persist.
 *
 * `records` may be a PREFIX of the errored set (one capped request). An
 * excluded record outside it is not subtracted, so the result can overstate
 * what blocks — the fail-closed direction.
 */
export const unresolvedErroredFromServer = (
  serverTotal: number,
  records: readonly ExtractedRecord[],
  excluded: ReadonlySet<number>
): number => {
  if (serverTotal === 0) return 0;
  let excludedErrored = 0;
  for (const record of records) {
    const index = absoluteIndexOf(record);
    if (index !== undefined && excluded.has(index)) excludedErrored++;
  }
  return Math.max(0, serverTotal - excludedErrored);
};

export interface GateInputs {
  /** The `?errored=true` endpoint's whole-document total. 0 when unavailable. */
  serverTotal: number;
  /** unresolvedErroredFromServer's result, passed in so it is computed once. */
  serverUnresolved: number;
  /** Whether the errored read returned fewer records than the total. */
  serverTruncated: boolean;
  /** record_summary.records_with_errors, or undefined on a pre-#6755 row. */
  summaryErroredTotal: number | undefined;
  /** DISTINCT loaded records with errors, via summarizeRecords. */
  loadedErroredCount: number;
  /** Whether more record pages exist than are loaded. */
  hasMoreRecords: boolean;
}

/**
 * What the approve gate blocks on.
 *
 * PREFERS THE SERVER'S ERRORED READ, because it is the only source carrying
 * absolute indices — and therefore the only one exclusions can be subtracted
 * from. The old summary subtraction survives strictly as the fallback for when
 * that read is unavailable: pre-000220 derived columns (the endpoint 409s), a
 * fetch failure, or genuinely zero errored records. Those three are
 * indistinguishable here, so the fallback is exclusion-blind and over-blocks
 * rather than under-blocks.
 */
export const unresolvedErroredForGate = ({
  serverTotal,
  serverUnresolved,
  serverTruncated,
  summaryErroredTotal,
  loadedErroredCount,
  hasMoreRecords,
}: GateInputs): UnresolvedErrored => {
  if (serverTotal > 0) {
    return { count: serverUnresolved, exact: !serverTruncated };
  }

  // Absent summary, or everything already loaded: the loaded-side errorCount
  // gates on its own and is exclusion-aware, so contributing 0 here is
  // correct rather than lenient.
  if (summaryErroredTotal === undefined || !hasMoreRecords) return { count: 0, exact: true };

  return { count: Math.max(0, summaryErroredTotal - loadedErroredCount), exact: false };
};
